/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box } from 'grommet';
import { LoadingSpinner } from '@blend/enterprise-components';
import * as RonAPI from 'blb-api/ron';
import * as Sentry from '@sentry/browser';
import { CredentialInfoCard } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/CredentialInfoCard';
import { AlertBar } from 'blb-components/GrommetComponents/AlertBar';
import { CredentialOptional, getLatestCredentialsByType } from 'blb-utils/notaryCredentials';
import { UserCredentialType } from 'blb-constants/ron';
import * as Ron from 'blb-ui-types/ron';
import { EditCredentialModal } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/EditCredentialModal';

/** This component renders the Credentials tab in Profile Page */
export const CredentialInfo: React.FC<{}> = () => {
  const [credentials, setCredentials] = useState<CredentialOptional[]>();
  const [loadingError, setLoadingError] = useState<string>();
  const [editingCredential, setEditingCredential] = useState<{
    credential: Ron.API.UserCredential | undefined;
    type: UserCredentialType;
  }>();

  const fetchData = async () => {
    try {
      const currentCredentials = await RonAPI.getCurrentUserCredentials();
      setCredentials(getLatestCredentialsByType(currentCredentials));
    } catch (error) {
      const err = error as Error;
      Sentry.captureException(err);
      setLoadingError(err.message);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData();
  }, []);

  const editCredentialModal = editingCredential && (
    <EditCredentialModal
      credential={editingCredential.credential}
      credentialType={editingCredential.type}
      handleClose={() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchData();
        setEditingCredential(undefined);
      }}
    />
  );

  if (loadingError) {
    return <AlertBar errorMessage={'loadingError'} id="it-alertBar" />;
  }
  if (!credentials) {
    return (
      <Box align="center" height="medium" justify="center" width="100%">
        <LoadingSpinner size="lg" theme="secondary" />
      </Box>
    );
  }
  return (
    <Box gap="small" margin="xsmall">
      {editCredentialModal}
      {credentials.map(item => (
        <CredentialInfoCard
          credential={item.credential}
          key={item.credentialType}
          onEditCredential={() => setEditingCredential({ credential: item.credential, type: item.credentialType })}
          type={item.credentialType}
        />
      ))}
    </Box>
  );
};
